import apiClient from './apiClient';
import { API_URL } from '../config';

export interface XcentricWorklistItem {
  study_id: number;
  sr_pacs_id: string;
  session_id: string | null;
  patient_name: string | null;
  patient_mrn: string | null;
  accession_number: string | null;
  modality: string | null;
  study_description: string | null;
  study_date: string | null;
  study_priority: string | null;
  status: string;
  xcentic_status: string | null;
  viewer_url: string | null;
  claimed_by: string | null;
  report_id: number | null;
  report_status: string | null;
  writeback_status: string | null;
  writeback_error: string | null;
  updated_at: string | null;
}

export interface XcentricWorklistResponse {
  items: XcentricWorklistItem[];
  total: number;
  viewer_configured: boolean;
  integration_mode: string;
  worklist_sync_enabled: boolean;
}

export interface XcentricSyncResponse {
  found: number;
  imported: number;
  created: number;
  failed: number;
  results: Array<{ sr_pacs_id: string; imported: boolean; created: boolean; error: string | null }>;
}

export interface XcentricConnectionTest {
  success: boolean;
  message: string;
  latency_ms: number | null;
}

/** Xcentic Q-3 date-only searches accept a range of at most 31 days. */
export const studyDateRange = (days: number, today: Date = new Date()): string => {
  const clamped = Math.max(1, Math.min(31, Math.floor(days)));
  const start = new Date(today);
  start.setDate(today.getDate() - (clamped - 1));
  const fmt = (d: Date) =>
    `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
  return `${fmt(start)}-${fmt(today)}`;
};

const xcentricService = {
  async getWorklist(status?: string): Promise<XcentricWorklistResponse> {
    const response = await apiClient.get(`${API_URL}/xcentric/worklist`, {
      params: status ? { status } : undefined,
    });
    return response.data;
  },

  async syncWorklist(days: number): Promise<XcentricSyncResponse> {
    const response = await apiClient.post(`${API_URL}/xcentric/worklist/sync`, {
      query: { StudyDate: studyDateRange(days) },
      limit: 200,
    });
    return response.data;
  },

  /** Xcentic study behind a dictation session; null when the session is not from Xcentic. */
  async getSessionStudy(sessionId: string): Promise<XcentricWorklistItem | null> {
    try {
      const response = await apiClient.get(`${API_URL}/xcentric/sessions/${sessionId}`);
      return response.data;
    } catch (err: unknown) {
      if ((err as { response?: { status?: number } })?.response?.status === 404) return null;
      throw err;
    }
  },

  async testConnection(): Promise<XcentricConnectionTest> {
    const response = await apiClient.post(`${API_URL}/xcentric/test-connection`);
    return response.data;
  },

  async retryWriteback(reportId: number): Promise<{ status: string; duplicate: boolean | null }> {
    const response = await apiClient.post(`${API_URL}/xcentric/writeback/${reportId}/retry`);
    return response.data;
  },
};

export default xcentricService;
